'use client';

import { NextIntlClientProvider, type AbstractIntlMessages } from 'next-intl';
import type { ReactNode } from 'react';

import type { Locale } from './config';
import {
  createOnceGate,
  formatMessageReport,
  isIgnoredError,
  selectMessageFallback,
} from './fallback';
import { I18nReadyContext } from './ready-context';

const shouldReport = createOnceGate();

interface ClientI18nProviderProps {
  locale: Locale;
  messages: AbstractIntlMessages;
  timeZone?: string;
  children: ReactNode;
}

/**
 * The single `NextIntlClientProvider` for client components. It is a client
 * component so it can set `onError` / `getMessageFallback` (functions cannot be
 * passed from a server component), which means it cannot inherit anything:
 * `locale`, `messages` and `timeZone` are passed in explicitly by `I18nProvider`.
 * A missing string renders empty and is warned about once per key in the console
 * (all environments): `packages/i18n` has no Sentry dependency. A key missing
 * from `messages` is looked up in the English registered by shared packages first.
 * Also marks that a provider exists (`I18nReadyContext`), which `useText` reads.
 */
export function ClientI18nProvider({
  locale,
  messages,
  timeZone,
  children,
}: ClientI18nProviderProps) {
  return (
    <NextIntlClientProvider
      locale={locale}
      messages={messages}
      timeZone={timeZone}
      onError={(error) => {
        if (isIgnoredError(error)) return;
        const report = formatMessageReport(error, locale);
        if (!shouldReport(report)) return;
        // eslint-disable-next-line no-console -- missing translations are reported once per key, in every environment
        console.warn(report);
      }}
      getMessageFallback={({ error, namespace, key }) =>
        selectMessageFallback({ error, namespace, key })
      }
    >
      <I18nReadyContext.Provider value>{children}</I18nReadyContext.Provider>
    </NextIntlClientProvider>
  );
}
